import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../api/client";

// Admin: proxy.market traffic (2026-10-20). Read-only; buying is done in the proxy.market cabinet.
const GB = 1024 ** 3;
const MB = 1024 ** 2;

function gb(v: number | null | undefined) {
  return v === null || v === undefined ? "—" : `${(v / GB).toFixed(2)} ГБ`;
}

function mbOrGb(v: number) {
  return v >= GB ? `${(v / GB).toFixed(2)} ГБ` : `${Math.round(v / MB)} МБ`;
}

function date(v: string | null) {
  if (!v) return "—";
  return new Date(v).toLocaleString("ru-RU", { day: "2-digit", month: "2-digit", year: "numeric" });
}

function dayLabel(d: string) {
  const [, m, dd] = d.split("-");
  return `${dd}.${m}`;
}

export default function AdminProxy() {
  const navigate = useNavigate();
  const [d, setD] = useState<any>(null);
  const [error, setError] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = (refresh: boolean) => {
    setBusy(true);
    api.get(`/admin/proxy${refresh ? "?refresh=1" : ""}`)
      .then((r: any) => { setD(r.data); setError(false); })
      .catch(() => setError(true))
      .finally(() => setBusy(false));
  };

  useEffect(() => { load(false); }, []);

  const s = d?.summary;
  const maxDay = d?.usage?.length ? Math.max(...d.usage.map((u: any) => u.bytes), 1) : 1;
  const low = s && s.days_left !== null && s.days_left < 5;

  return (
    <div style={styles.container}>
      <button style={styles.backBtn} onClick={() => navigate("/admin")}>← Админ-панель</button>
      <h1 style={styles.title}>Прокси</h1>
      {error && <div style={styles.hint}>Не удалось загрузить данные</div>}
      {!d && !error && <div style={styles.center}><div style={styles.spinner} /></div>}
      {d && (
        <>
          {d.errors?.length > 0 && (
            <div style={styles.warn}>{d.errors.map((e: string) => <div key={e}>⚠️ {e}</div>)}</div>
          )}

          {s && (
            <div style={styles.box}>
              <div style={styles.big}>{gb(s.remaining)}</div>
              <div style={styles.hint}>осталось{s.total ? ` из ${gb(s.total)}` : ""}</div>
              {s.total > 0 && (
                <div style={styles.barOuter}>
                  <div style={{ ...styles.barInner, width: `${Math.max(2, s.remaining_pct ?? 0)}%`, backgroundColor: low ? "#ef4444" : "#10b981" }} />
                </div>
              )}
              <div style={styles.row}><span style={styles.label}>Хватит примерно на</span>
                <span style={{ ...styles.value, color: low ? "#ef4444" : undefined }}>{s.days_left === null ? "—" : `${s.days_left.toFixed(1)} дн.`}</span></div>
              <div style={styles.row}><span style={styles.label}>Пакет действует до</span><span style={styles.value}>{date(s.expires_at)}</span></div>
              <div style={styles.row}><span style={styles.label}>Расход сегодня</span><span style={styles.value}>{mbOrGb(s.today)}</span></div>
              <div style={styles.row}><span style={styles.label}>В среднем за сутки</span><span style={styles.value}>{mbOrGb(s.avg_day)}</span></div>
              <div style={styles.row}><span style={styles.label}>За 7 дней</span><span style={styles.value}>{mbOrGb(s.week)}</span></div>
              <div style={styles.row}><span style={styles.label}>Деньги на счёте proxy.market</span>
                <span style={styles.value}>{d.balance === null ? "—" : `${Number(d.balance).toLocaleString("ru-RU")} ₽`}</span></div>
            </div>
          )}

          {d.usage?.length > 0 && (
            <div style={styles.box}>
              <div style={styles.boxTitle}>Расход по дням</div>
              {d.usage.map((u: any) => (
                <div key={u.day} style={styles.dayRow}>
                  <span style={styles.dayLabel}>{dayLabel(u.day)}</span>
                  <div style={styles.dayBarOuter}><div style={{ ...styles.dayBar, width: `${(u.bytes / maxDay) * 100}%` }} /></div>
                  <span style={styles.dayValue}>{mbOrGb(u.bytes)}</span>
                </div>
              ))}
            </div>
          )}

          {d.packages?.length > 0 && (
            <div style={styles.box}>
              <div style={styles.boxTitle}>Пакеты</div>
              {d.packages.map((p: any) => (
                <div key={p.id} style={styles.pkg}>
                  <div style={styles.value}>{p.name || `Пакет #${p.id}`}</div>
                  <div style={styles.hint}>
                    {p.total !== null ? `осталось ${gb(p.remaining)} из ${gb(p.total)}` : `оплата по факту, израсходовано ${gb(p.used)}`}
                    {" · "}до {date(p.expires_at)}{p.expired ? " · истёк" : ""}{!p.is_active ? " · не активен" : ""}
                  </div>
                </div>
              ))}
            </div>
          )}

          {d.prices?.length > 0 && (
            <div style={styles.box}>
              <div style={styles.boxTitle}>Цены на трафик</div>
              {d.prices.map((p: any) => (
                <div key={p.gb} style={styles.row}>
                  <span style={styles.label}>{p.gb} ГБ</span>
                  <span style={styles.value}>{p.price.toLocaleString("ru-RU")} ₽{p.gb ? ` (${Math.round(p.price / p.gb)} ₽/ГБ)` : ""}</span>
                </div>
              ))}
            </div>
          )}

          <a href={d.cabinet_url} target="_blank" rel="noopener noreferrer" style={styles.actionBtn}>Купить или продлить в кабинете proxy.market</a>
          <button style={styles.secondaryBtn} onClick={() => load(true)} disabled={busy}>{busy ? "Обновляю…" : "Обновить сейчас"}</button>
          <div style={{ ...styles.hint, marginTop: "8px", textAlign: "center" }}>
            Данные: {new Date(d.updated_at).toLocaleString("ru-RU")}{d.cached ? " (из кэша, обновляются раз в 10 мин)" : ""}
          </div>
        </>
      )}
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: { padding: "16px", maxWidth: "480px", margin: "0 auto" },
  backBtn: { background: "none", border: "none", color: "var(--tg-theme-link-color, #2481cc)", fontSize: "14px", cursor: "pointer", padding: 0, marginBottom: "12px" },
  title: { fontSize: "22px", fontWeight: 700, margin: "0 0 12px 0", color: "var(--tg-theme-text-color, #000)" },
  center: { display: "flex", justifyContent: "center", padding: "40px 0" },
  spinner: { width: "28px", height: "28px", border: "3px solid #e5e7eb", borderTop: "3px solid #3b82f6", borderRadius: "50%", animation: "spin 1s linear infinite" },
  hint: { fontSize: "12px", color: "var(--tg-theme-hint-color, #666)" },
  warn: { backgroundColor: "#fef3c7", color: "#92400e", borderRadius: "10px", padding: "10px", fontSize: "12px", marginBottom: "10px" },
  box: { backgroundColor: "var(--tg-theme-secondary-bg-color, #fff)", borderRadius: "12px", padding: "12px", marginBottom: "10px", boxShadow: "0 1px 3px rgba(0,0,0,0.08)" },
  boxTitle: { fontSize: "13px", fontWeight: 700, color: "var(--tg-theme-text-color, #000)", marginBottom: "8px", textTransform: "uppercase" },
  big: { fontSize: "30px", fontWeight: 800, color: "var(--tg-theme-text-color, #000)" },
  barOuter: { height: "8px", borderRadius: "4px", backgroundColor: "rgba(127,127,127,0.2)", margin: "10px 0" },
  barInner: { height: "8px", borderRadius: "4px" },
  row: { display: "flex", justifyContent: "space-between", gap: "12px", padding: "3px 0", fontSize: "13px" },
  label: { color: "var(--tg-theme-hint-color, #666)" },
  value: { color: "var(--tg-theme-text-color, #000)", fontWeight: 600, textAlign: "right" },
  dayRow: { display: "flex", alignItems: "center", gap: "8px", padding: "3px 0", fontSize: "12px" },
  dayLabel: { width: "40px", color: "var(--tg-theme-hint-color, #666)" },
  dayBarOuter: { flex: 1, height: "10px", borderRadius: "5px", backgroundColor: "rgba(127,127,127,0.15)" },
  dayBar: { height: "10px", borderRadius: "5px", backgroundColor: "#3b82f6" },
  dayValue: { width: "64px", textAlign: "right", color: "var(--tg-theme-text-color, #000)" },
  pkg: { padding: "4px 0" },
  actionBtn: { display: "block", textAlign: "center", textDecoration: "none", padding: "12px", borderRadius: "10px", backgroundColor: "var(--tg-theme-button-color, #2481cc)", color: "var(--tg-theme-button-text-color, #fff)", fontSize: "14px", fontWeight: 600, marginTop: "4px" },
  secondaryBtn: { width: "100%", padding: "10px", borderRadius: "10px", border: "none", backgroundColor: "var(--tg-theme-secondary-bg-color, #fff)", color: "var(--tg-theme-link-color, #2481cc)", fontSize: "14px", cursor: "pointer", marginTop: "8px" },
};
